import React from 'react';
import { Spinner } from 'react-bootstrap';

import InteractiveMap, {
  ESRI_SATELLITE_TILE_ATTRIBUTION,
  ESRI_SATELLITE_TILE_URL,
} from 'components/InteractiveMap';
import BrazilStatesLayer from 'components/InteractiveMap/BrazilStatesLayer';
import BrazilCountryLayer from 'components/InteractiveMap/BrazilCountryLayer';
import CanvasHeatLayer from 'components/InteractiveMap/CanvasHeatLayer';
import QueryErrorState from 'components/DesignSystem/QueryErrorState';

import useMapData from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useMapData';
import useMapLocationMarkers from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useMapLocationMarkers';
import useCaption from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useCaption';
import useEnrichedMarkers from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useEnrichedMarkers';
import useVariables from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useVariables';
import useHeatmapLegend from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useHeatmapLegend';

import './index.scss';

export interface MapDisplayProps {
  layers: Record<string, boolean>;
  setOnDownloadClick: React.Dispatch<React.SetStateAction<() => void>>;
  mapFilename: string;
}

/**
 * Versão mobile do mapa do balanço hídrico sequencial. Reaproveita todos os hooks de
 * dados e as camadas de renderização do desktop, mas sem o painel flutuante de camadas
 * (que no mobile vira o bottom sheet "Camadas") e sem o hover card / tooltip — touch não
 * tem hover. O estado das camadas é controlado de fora (prop `layers`).
 */
export default function MapDisplay({
  layers,
  setOnDownloadClick,
  mapFilename,
}: MapDisplayProps) {
  const {
    mapData, queryError, onRetry, loading,
  } = useMapData();
  const locationMarkers = useMapLocationMarkers();
  const caption = useCaption();
  const { variableLabel, unit, config } = useVariables();
  const legend = useHeatmapLegend(mapData);
  const enrichedMarkers = useEnrichedMarkers(locationMarkers, mapData, variableLabel ?? '', unit);

  return (
    <div className="agro-wbs-maps-mobile-map">
      <InteractiveMap
        markers={layers.localidades ? enrichedMarkers : []}
        tileUrl={ESRI_SATELLITE_TILE_URL}
        tileAttribution={ESRI_SATELLITE_TILE_ATTRIBUTION}
      >
        <CanvasHeatLayer
          points={(!loading && mapData) || []}
          config={config}
          legend={legend}
        />
        {layers.limitesEstaduais ? <BrazilStatesLayer /> : <BrazilCountryLayer />}
        <InteractiveMap.ImageDownloader
          setOnDownload={setOnDownloadClick}
          filename={mapFilename}
          caption={caption}
        />
      </InteractiveMap>

      {queryError && (
        <div className="agro-map-error">
          <QueryErrorState
            title={queryError.data}
            message={queryError.description}
            onRetry={onRetry}
          />
        </div>
      )}

      {loading && (
        <div className="agro-map-loading">
          <Spinner animation="border" />
        </div>
      )}
    </div>
  );
}
